import { mutatorAction } from "satcheljs";
import { debounce } from "owa-debounce";
import { getStore } from "../store/findInMessageStore";
import getTotalNumberOfMatches from "../selectors/getTotalNumberOfMatches";
import type { LastActionPerformed } from "../store/schema/LastActionPerformed";
import getFindInMessageViewState from "../selectors/getFindInMessageViewState";

// this mutator is called when the ctrl+f popout becomes visible.
// it will update the isCalloutVisible property of the FindInMessageViewState
export const showFindInMessageCalloutMutator: (context: string) => void = mutatorAction("showFindInMessageCalloutMutator", (context: string): void => {
    const store = getStore();
    const viewState = store.findInMessageViewStates.get(context);
    if (viewState) {
        viewState.isCalloutVisible = true;
    }
});

// this mutator is called when the ctrl+f popout becomes hidden.
// it will reset some properties in the FindInMessageViewState
// (NOTE: we are NOT resetting the matchesPerItemId array here, because we want to keep the state of all the item parts in conversation mode)
export const hideFindInMessageCalloutMutator: (context: string) => void = mutatorAction("hideFindInMessageCalloutMutator", (context: string): void => {
    const store = getStore();
    const viewState = store.findInMessageViewStates.get(context);
    if (viewState) {
        viewState.isCalloutVisible = false;
        viewState.itemPartCollapsedExpandedIncrementor = 0;
        viewState.lastActionPerformed = undefined;
        viewState.searchString = "";
        viewState.numberOfMatches = 0;
        viewState.currentMatchIndex = 0;
    }
});

// this mutator is called when the ctrl+f popout needs to be cleaned up.
// it will remove the FindInMessageViewState from the store.
export const removeContextFromStoreMutator: (context: string) => void = mutatorAction("removeContextFromStoreMutator", (context: string): void => {
    const store = getStore();
    store.findInMessageViewStates.delete(context);
});

// this mutator is called when a user types in something the search box and either presses search (or enter) or enough time passes
// after the last character was typed. it will update the searchString property of the FindInMessageViewState
export const setSearchStringMutator: (context: string, searchString: string) => void = mutatorAction("setSearchStringMutator", (context: string, searchString: string): void => {
    const store = getStore();
    const viewState = store.findInMessageViewStates.get(context);
    if (viewState) {
        viewState.searchString = searchString;
    }
});

// this mutator is called when the number of matches for a given itemId changes.
export const setNumberOfMatchesPerItemIdMutator: (context: string, itemId: string, numberOfMatches: number, fromQuotedBody: boolean, currentMatchIndex?: number) => void = mutatorAction("setNumberOfMatchesPerItemIdMutator", (context: string, itemId: string, numberOfMatches: number, fromQuotedBody: boolean, currentMatchIndex?: number): void => {
    const store = getStore();
    const viewState = store.findInMessageViewStates.get(context);
    
    if (!viewState) {
        return;
    }
    
    // check to see if we are in conversation mode (if there are multiple item parts). In this case matchesPerItemId array would not be empty)
    if (viewState?.matchesPerItemId && (viewState?.matchesPerItemId?.length ?? 0 > 0)) {
        for (const [_, value] of viewState.matchesPerItemId?.entries()) {
            if (value.itemId === itemId && value.fromQuotedBody === fromQuotedBody) {
                value.numberOfMatches = numberOfMatches;
                break;
            }
        }
        
        // also recalculate and update the value of total number of matches in the store
        setNumberOfMatchesDebounced(context, getTotalNumberOfMatches(context));
    }
    else {
        // If we are in item view mode just update the total number of matches in the store
        viewState.numberOfMatches = numberOfMatches;
    }
    
    if (currentMatchIndex !== undefined) {
        viewState.currentMatchIndex = currentMatchIndex;
    }
});

// this mutator is called when the user clicks on the item part to expand/collapse it.
export const setIsExpandedItemPartMutator: (context: string, itemId: string, isExpanded: boolean, fromQuotedBody: boolean) => void = mutatorAction("setIsExpandedItemPartMutator", (context: string, itemId: string, isExpanded: boolean, fromQuotedBody: boolean): void => {
    const store = getStore();
    const viewState = store.findInMessageViewStates.get(context);
    if (viewState?.matchesPerItemId) {
        for (const [_, value] of viewState.matchesPerItemId?.entries()) {
            if (value.itemId === itemId && value.fromQuotedBody === fromQuotedBody) {
                // update the isExpanded property of the item part in the store
                value.isExpanded = isExpanded;
                // also set the number of matches for the item part to 0 if it's collapsed
                if (!isExpanded) {
                    value.numberOfMatches = 0;
                }
                // increment the itemPartCollapsedExpandedIncrementor property. This is used as a notification mechanism
                // that an update was made
                viewState.itemPartCollapsedExpandedIncrementor =
                    (viewState.itemPartCollapsedExpandedIncrementor ?? 0) + 1;
                break;
            }
        }
        
        // also recalculate and update the value of total number of matches in the store
        viewState.numberOfMatches = getTotalNumberOfMatches(context);
    }
});

// this mutator is called when a user navigates to the next or previous search result
export const setCurrentMatchIndexMutator: (context: string, currentMatchIndex: number) => void = mutatorAction("setCurrentMatchIndexMutator", (context: string, currentMatchIndex: number): void => {
    const store = getStore();
    const viewState = store.findInMessageViewStates.get(context);
    if (viewState) {
        viewState.currentMatchIndex = currentMatchIndex;
    }
});

// this mutator is called to initialize the matchesPerItemId array in the store.
export const initializeMatchesPerItemIdMutator: (context: string, conversationNodeIds: string[], isSettingNewestOnBottom: boolean) => void = mutatorAction("initializeMatchesPerItemIdMutator", (context: string, conversationNodeIds: string[], isSettingNewestOnBottom: boolean): void => {
    const viewState = getFindInMessageViewState(context);
    
    if (viewState) {
        // initialization is different for first element whether we are in newest on top or bottom modes
        if (conversationNodeIds.length > 0) {
            const firstConversationNodeId = conversationNodeIds[0];
            
            if (isSettingNewestOnBottom) {
                // for newest on bottom mode, the quoted body element of the first item comes first
                viewState.matchesPerItemId = [
                    {
                        itemId: firstConversationNodeId,
                        numberOfMatches: 0,
                        isExpanded: true,
                        fromQuotedBody: true,
                    },
                    {
                        itemId: firstConversationNodeId,
                        numberOfMatches: 0,
                        isExpanded: true,
                        fromQuotedBody: false,
                    },
                ];
            }
            else {
                // for newest on top mode, the quoted body element of the first item comes after the item
                viewState.matchesPerItemId = [
                    {
                        itemId: firstConversationNodeId,
                        numberOfMatches: 0,
                        isExpanded: true,
                        fromQuotedBody: false,
                    },
                    {
                        itemId: firstConversationNodeId,
                        numberOfMatches: 0,
                        isExpanded: true,
                        fromQuotedBody: true,
                    },
                ];
            }
        }
        
        // for all other items, the quoted body element comes after the item, for both newest on top and bottom modes
        for (let i = 1; i < conversationNodeIds.length; i++) {
            const conversationNodeId = conversationNodeIds[i];
            viewState.matchesPerItemId?.push({
                itemId: conversationNodeId,
                numberOfMatches: 0,
                isExpanded: true,
                fromQuotedBody: false,
            });
            viewState.matchesPerItemId?.push({
                itemId: conversationNodeId,
                numberOfMatches: 0,
                isExpanded: false,
                fromQuotedBody: true,
            });
        }
    }
});

// this mutator is called to update the last user action that was performed
export const setLastActionPerformedMutator: (context: string, lastActionPerformed: LastActionPerformed) => void = mutatorAction("setLastActionPerformedMutator", (context: string, lastActionPerformed: LastActionPerformed): void => {
    const store = getStore();
    const viewState = store.findInMessageViewStates.get(context);
    if (viewState) {
        viewState.lastActionPerformed = lastActionPerformed;
    }
});
// this function is called to update the number of matches in the store.
// some conversations can have a lot of item parts, when a mutator is called for each item part there could be a too many rerenders exception.
// for that reason we are debouncing the calls to the mutator
const setNumberOfMatchesDebounced = debounce(mutatorAction("setNumberOfMatchesDebounced", (context: string, numberOfMatches: number) => {
    const store = getStore();
    const viewState = store.findInMessageViewStates.get(context);
    if (viewState) {
        viewState.numberOfMatches = numberOfMatches;
    }
}), 500);
